import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// Account self-service: in-app account deletion (App Store Guideline
// 5.1.1(v)). The `delete-account` edge function deletes the CALLER'S OWN auth
// user — it never accepts a target id — and returns `{ ok: true }`.

export interface DeleteAccountResult {
  ok: boolean;
}

function throwFunctionError(error: unknown): never {
  if (error instanceof Error) throw error;
  throw new Error(typeof error === 'string' ? error : 'Edge Function request failed');
}

/**
 * Permanently deletes the signed-in user's account, then signs them out and
 * sends them to /login. The edge function operates strictly on the verified
 * caller, so no arguments are accepted. Deletion is immediate and irreversible;
 * legally-retained COPPA consent records are preserved server-side.
 */
export function useDeleteAccount() {
  return useMutation({
    mutationFn: async (): Promise<void> => {
      const result = (await supabase.functions.invoke<DeleteAccountResult>('delete-account', {
        body: {},
      })) as { data: DeleteAccountResult | null; error: unknown };
      if (result.error) throwFunctionError(result.error);
      if (result.data?.ok !== true) {
        throw new Error('Account deletion did not complete. Please try again.');
      }
    },
    onSuccess: async () => {
      // The auth user is gone; sign out to clear the now-invalid local session,
      // then leave the app. We swallow signOut errors because the account is
      // already deleted — the user must not stay on an authenticated screen.
      try {
        await supabase.auth.signOut();
      } catch {
        // ignore — account is deleted regardless of local sign-out outcome.
      }
      window.location.assign('/login');
    },
  });
}
